import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, ShieldCheck, Package } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/data/products";

const ORDERS_KEY = "innayaa_orders_v1";

function generateOrderId() {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `INN${yy}${mm}${dd}-${rand}`;
}

export default function Checkout() {
  const { items, subtotal } = useCart();
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    pincode: "",
    notes: "",
  });

  const shipping = subtotal > 0 && subtotal < 4000 ? 150 : 0;
  const total = subtotal + shipping;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.fullName.trim()) return "Please enter your full name";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email";
    if (!/^[6-9]\d{9}$/.test(form.phone.replace(/\D/g, "")))
      return "Please enter a valid 10-digit Indian mobile number";
    if (!form.address1.trim()) return "Please enter your address";
    if (!form.city.trim()) return "Please enter your city";
    if (!form.state.trim()) return "Please enter your state";
    if (!/^\d{6}$/.test(form.pincode.trim())) return "Please enter a valid 6-digit PIN code";
    return null;
  };

  const onPlaceOrder = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setPlacing(true);
    const orderId = generateOrderId();
    const order = {
      id: orderId,
      placedAt: new Date().toISOString(),
      items: items.map((i) => ({
        id: i.id,
        slug: i.slug,
        name: i.name,
        price: i.price,
        quantity: i.quantity,
        image: i.image,
      })),
      customer: { ...form },
      totals: { subtotal, shipping, codFee: 0, total },
      payment: { method: "PhonePe", status: "pending" },
    };

    // Persist locally first — order confirmation page can still render even if backend is down.
    try {
      const existing = JSON.parse(localStorage.getItem(ORDERS_KEY) || "[]");
      existing.unshift(order);
      localStorage.setItem(ORDERS_KEY, JSON.stringify(existing));
    } catch (e2) {
      // ignore storage failure
    }

    const apiBase = process.env.REACT_APP_BACKEND_URL;

    // Always persist the order first (so backend has it before payment redirect)
    if (apiBase) {
      try {
        await fetch(`${apiBase}/api/orders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: order.id,
            items: order.items,
            customer: order.customer,
            totals: order.totals,
          }),
        });
      } catch (e3) {
        // eslint-disable-next-line no-console
        console.warn("Order backend unreachable — kept locally:", e3.message);
      }
    }

    // PhonePe online payment — redirect to hosted checkout
    if (apiBase) {
      try {
        const r = await fetch(`${apiBase}/api/payments/phonepe/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            order_id: order.id,
            amount_inr: total,
            customer_name: form.fullName,
            customer_email: form.email,
            customer_phone: form.phone,
          }),
        });
        const data = await r.json();
        if (!r.ok || !data.redirectUrl) {
          throw new Error(data.detail || "Unable to start PhonePe payment");
        }
        // We don't clear the cart yet — only clear on successful payment return
        toast.success("Redirecting to PhonePe…");
        window.location.assign(data.redirectUrl);
        return;
      } catch (err) {
        setPlacing(false);
        toast.error(err.message || "Payment could not be started. Please try again in a moment.");
        return;
      }
    }

    // If the backend URL isn't configured, don't leave the user hanging
    setPlacing(false);
    toast.error("Online payment is temporarily unavailable. Please try again shortly.");
    navigate(`/cart`);
  };

  if (items.length === 0 && !placing) {
    return (
      <div data-testid="checkout-empty" className="max-w-[600px] mx-auto px-5 sm:px-6 py-24 md:py-32 text-center">
        <p className="overline">Your bag is empty</p>
        <h1 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-[#2A2626]">
          Nothing to check out yet.
        </h1>
        <p className="mt-4 text-[#5C564F]">Add a piece you love, and we&rsquo;ll take it from here.</p>
        <Link
          to="/shop"
          className="mt-10 inline-block bg-[#2A2626] text-[#FAF8F5] px-10 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
        >
          Browse the collection
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="bg-[#FAF8F5]">
      <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 pt-10 md:pt-16 pb-6">
        <p className="overline">Checkout · Secure online payment</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#2A2626] leading-none">
          Almost yours.
        </h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Form */}
          <form
            onSubmit={onPlaceOrder}
            data-testid="checkout-form"
            noValidate
            className="lg:col-span-7 space-y-10"
          >
            {/* Contact */}
            <fieldset className="space-y-6">
              <legend className="overline mb-4">Contact</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  testid="checkout-fullname"
                  label="Full name"
                  value={form.fullName}
                  onChange={onChange("fullName")}
                  required
                />
                <Input
                  testid="checkout-phone"
                  label="Mobile number"
                  value={form.phone}
                  onChange={onChange("phone")}
                  placeholder="10-digit mobile"
                  required
                  inputMode="tel"
                />
              </div>
              <Input
                testid="checkout-email"
                label="Email"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                placeholder="you@example.com"
                required
              />
            </fieldset>

            {/* Shipping */}
            <fieldset className="space-y-6">
              <legend className="overline mb-4">Shipping address</legend>
              <Input
                testid="checkout-address1"
                label="Address line 1"
                value={form.address1}
                onChange={onChange("address1")}
                placeholder="House / Flat, Building, Street"
                required
              />
              <Input
                testid="checkout-address2"
                label="Address line 2 (optional)"
                value={form.address2}
                onChange={onChange("address2")}
                placeholder="Landmark, Locality"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  testid="checkout-city"
                  label="City"
                  value={form.city}
                  onChange={onChange("city")}
                  required
                />
                <Input
                  testid="checkout-state"
                  label="State"
                  value={form.state}
                  onChange={onChange("state")}
                  required
                />
                <Input
                  testid="checkout-pincode"
                  label="PIN code"
                  value={form.pincode}
                  onChange={onChange("pincode")}
                  placeholder="6 digits"
                  required
                  inputMode="numeric"
                />
              </div>
              <div>
                <label className="overline">Order notes (optional)</label>
                <textarea
                  data-testid="checkout-notes"
                  rows={3}
                  value={form.notes}
                  onChange={onChange("notes")}
                  className="mt-3 w-full bg-transparent border-b border-[#DCD5CA] py-3 focus:outline-none focus:border-[#2A2626] font-sans text-[#2A2626] resize-none"
                />
              </div>
            </fieldset>

            {/* Payment */}
            <fieldset>
              <legend className="overline mb-4">Payment</legend>

              <div
                data-testid="payment-online-only"
                className="block border border-[#2A2626] bg-[#F0EBE1]/70 p-5 md:p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-4 h-4 rounded-full border-2 border-[#2A2626] mt-1 shrink-0 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-[#2A2626]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-serif text-xl text-[#2A2626]">Pay online with PhonePe</p>
                    <p className="mt-1.5 text-sm text-[#5C564F] leading-relaxed">
                      UPI, cards, net banking and wallets — secured by PhonePe. You&rsquo;ll be
                      redirected to complete the payment.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.22em] text-[#5C564F]">
                      <span>UPI</span><span>·</span><span>Cards</span><span>·</span>
                      <span>Net Banking</span><span>·</span><span>Wallets</span>
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>

            <button
              type="submit"
              disabled={placing}
              data-testid="place-order-button"
              className="w-full bg-[#2A2626] text-[#FAF8F5] py-4 md:py-5 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {placing ? "Redirecting to PhonePe…" : `Pay ${formatINR(total)} with PhonePe`}
            </button>

            <p className="text-[11px] text-[#5C564F] text-center">
              By placing this order you agree to Innayaa&rsquo;s Terms &amp; refund policy.
            </p>
          </form>

          {/* Summary */}
          <aside className="lg:col-span-5">
            <div className="bg-[#F0EBE1]/70 p-6 md:p-8 lg:sticky lg:top-28">
              <p className="overline">Your bag ({items.length})</p>
              <ul className="mt-5 md:mt-6 space-y-4 max-h-[360px] overflow-y-auto pr-1">
                {items.map((i) => (
                  <li
                    key={i.id}
                    data-testid={`checkout-item-${i.slug}`}
                    className="flex gap-4"
                  >
                    <div className="w-16 h-20 bg-[#FAF8F5] shrink-0 overflow-hidden">
                      <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-[15px] text-[#2A2626] leading-tight truncate">
                        {i.name}
                      </p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#5C564F]">
                        Qty {i.quantity}
                      </p>
                    </div>
                    <p className="text-sm text-[#2A2626] shrink-0">
                      {formatINR(i.price * i.quantity)}
                    </p>
                  </li>
                ))}
              </ul>

              <dl className="mt-6 pt-5 border-t border-[#DCD5CA] space-y-2.5 text-sm">
                <Row label="Subtotal" value={formatINR(subtotal)} testid="summary-subtotal" />
                <Row
                  label="Shipping"
                  value={shipping === 0 ? "Complimentary" : formatINR(shipping)}
                />
              </dl>
              <div className="border-t border-[#DCD5CA] mt-5 pt-5 flex items-baseline justify-between">
                <span className="overline">Total (PhonePe)</span>
                <span data-testid="summary-total" className="font-serif text-3xl text-[#2A2626]">
                  {formatINR(total)}
                </span>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-3">
                {[
                  { Icon: Truck, t: "Ships in 2–3 working days" },
                  { Icon: Package, t: "Signature Innayaa keepsake box" },
                  { Icon: ShieldCheck, t: "6-month care support included" },
                ].map(({ Icon, t }) => (
                  <div key={t} className="flex items-start gap-3">
                    <Icon size={16} strokeWidth={1.25} className="text-[#2A2626] mt-0.5" />
                    <span className="text-[13px] text-[#2A2626]">{t}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/cart"
                className="mt-6 inline-block text-[11px] uppercase tracking-[0.22em] text-[#5C564F] hover:text-[#2A2626] link-underline"
              >
                ← Back to bag
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Input({ testid, label, value, onChange, type = "text", required, placeholder, inputMode }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        inputMode={inputMode}
        className="mt-3 w-full bg-transparent border-b border-[#DCD5CA] py-3 focus:outline-none focus:border-[#2A2626] font-sans text-[#2A2626] placeholder:text-[#5C564F]/60"
      />
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between">
      <dt className="text-[#5C564F]">{label}</dt>
      <dd data-testid={testid} className="text-[#2A2626]">
        {value}
      </dd>
    </div>
  );
}
